// src/pages/StaffFeedbackFormPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import axios from '../api/axios';

const StarRating = ({ rating, setRating, size = 'text-2xl', disabled = false }) => (
  <div className="flex justify-center gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => !disabled && setRating(star)}
        disabled={disabled}
        className={`${size} transition-transform transform ${
          disabled 
            ? 'text-gray-400 cursor-not-allowed' 
            : 'hover:scale-110'
        } ${
          star <= rating ? 'text-yellow-400' : 'text-gray-300'
        }`}
      >
        ★
      </button>
    ))}
  </div>
);

export default function StaffFeedbackFormPage() {
  const location = useLocation();
  const params = useParams();
  
  const [formType, setFormType] = useState(''); // 'self' or 'peer'
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Self Evaluation State
  const [selfData, setSelfData] = useState({
    staffName: '',
    team: '',
    role: '',
    proactiveness: 0,
    taskCompletion: 0,
    communication: 0,
    timeManagement: 0,
    proudOf: '',
    teamChallenge: '',
    improvements: '',
    nextAction: ''
  });

  // Peer Evaluation State
  const [peerData, setPeerData] = useState({
    evaluatorTeam: '',
    mostHelpfulColleague: '',
    mostHelpfulReason: ''
  });

  const [selectedTeam, setSelectedTeam] = useState('');
  const [showTeamStaff, setShowTeamStaff] = useState(false);
  const [teamStaffList, setTeamStaffList] = useState([]);
  const [staffEvaluations, setStaffEvaluations] = useState({});
  const [excludedStaff, setExcludedStaff] = useState(new Set());

  const [staffList, setStaffList] = useState([]);

  // URL 파라미터 파싱
  const queryParams = new URLSearchParams(location.search);
  const eventId = params.eventId || queryParams.get('eventId');
  const branch = queryParams.get('branch');

  useEffect(() => {
    if (eventId && branch) {
      loadEventAndStaffData();
    }
  }, [eventId, branch]);

  const loadEventAndStaffData = async () => {
    try {
      // 이벤트 정보 로드
      const eventResponse = await axios.get(`/api/admin/events`);
      const event = eventResponse.data.find(e => String(e.id) === String(eventId));
      
      if (event) {
        setEventDetails(event);
      }

      // 스태프 목록 로드 (같은 지부의 스태프만)
      const staffResponse = await axios.get(`/api/staff/list?branch=${branch}`);
      setStaffList(staffResponse.data || []);
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleTeamSelection = (team) => {
    setSelectedTeam(team);
    setPeerData(prev => ({...prev, evaluatorTeam: team}));
    
    // 선택한 팀의 스태프 필터링
    const filteredStaff = staffList.filter(staff => {
      // team 값이 "GA", "PR", "EP"이고 staff.team이 "General Affairs" 등일 수 있음
      const teamMap = { 'GA': 'General Affairs', 'PR': 'Public Relations', 'EP': 'Event Planning' };
      const expectedTeam = teamMap[team] || team;
      const teamMatches = staff.team === expectedTeam || staff.team.includes(team);
      const roleMatches = staff.role !== 'LEADER' && staff.role !== 'PRESIDENT';
      return teamMatches && roleMatches;
    });
    
    setTeamStaffList(filteredStaff);
    setShowTeamStaff(true);
    
    // 각 스태프별 평가 데이터 초기화
    const initialEvaluations = {};
    filteredStaff.forEach(staff => {
      initialEvaluations[staff.name] = {
        contribution: 0,
        teamwork: 0,
        communication: 0,
        strengths: '',
        improvements: ''
      };
    });
    setStaffEvaluations(initialEvaluations);
  };

  const updateStaffEvaluation = (staffName, field, value) => {
    setStaffEvaluations(prev => ({
      ...prev,
      [staffName]: {
        ...prev[staffName],
        [field]: value
      }
    }));
  };

  const handleSelfSubmit = async (e) => {
    e.preventDefault();
    
    if (!selfData.staffName || !selfData.team) {
      alert('Please fill in your name and team.');
      return;
    }
    if (selfData.proactiveness === 0 || selfData.taskCompletion === 0 || 
        selfData.communication === 0 || selfData.timeManagement === 0) {
      alert('Please rate all performance areas (Proactiveness, Task Completion, Communication, Time Management).');
      return;
    }
    try {
      const submitData = {
        eventId: Number(eventId),
        ...selfData
      };
      const response = await axios.post('/api/feedback/staff/self/submit', submitData);
      setIsSubmitted(true);
    } catch (error) {
      alert('Failed to submit feedback: ' + (error.response?.data?.message || error.message));
    }
  };

  const handlePeerSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedTeam) {
      alert('Please select your team first.');
      return;
    }

    // 제외되지 않은 스태프에 대한 평가가 완료되었는지 확인
    const activeStaffList = teamStaffList.filter(staff => !excludedStaff.has(staff.name));
    const incompleteEvaluations = activeStaffList.filter(staff => {
      const evaluation = staffEvaluations[staff.name];
      return !evaluation || evaluation.contribution === 0 || evaluation.teamwork === 0 || evaluation.communication === 0;
    });

    if (incompleteEvaluations.length > 0) {
      alert('Please complete all required ratings for all team members.');
      return;
    }

    try {
      // 제외되지 않은 스태프별로 개별 피드백 제출
      const submissionPromises = activeStaffList.map(async (staff) => {
        const evaluation = staffEvaluations[staff.name];
        const submitData = {
          eventId: Number(eventId),
          evaluatorTeam: selectedTeam,
          evaluatedStaff: staff.name,
          contribution: evaluation.contribution,
          teamwork: evaluation.teamwork,
          communication: evaluation.communication,
          strengths: evaluation.strengths,
          improvements: evaluation.improvements
        };
        
        return axios.post('/api/feedback/staff/peer/submit', submitData);
      });

      // 모든 제출이 완료될 때까지 대기
      await Promise.all(submissionPromises);
      
      // Most Helpful Colleague 제출 (선택사항)
      if (peerData.mostHelpfulColleague) {
        const helpfulData = {
          eventId: Number(eventId),
          evaluatorTeam: selectedTeam,
          evaluatedStaff: peerData.mostHelpfulColleague, // ✅ evaluatedStaff 필드 추가
          contribution: 0,
          teamwork: 0,
          communication: 0,
          strengths: '',
          improvements: '',
          mostHelpfulColleague: peerData.mostHelpfulColleague,
          mostHelpfulReason: peerData.mostHelpfulReason || ''
        };
        await axios.post('/api/feedback/staff/peer/submit', helpfulData);
      }

      setIsSubmitted(true);
    } catch (error) {
      alert('Failed to submit feedback: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p>Loading...</p></div>;
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-3xl font-bold text-green-600 mb-4">Feedback Submitted Successfully!</h1>
          <p className="text-gray-600 mb-8">Thank you for your valuable feedback.</p>
          
          {formType === 'self' ? (
            // Self Evaluation 제출 후 - Peer Evaluation으로 이동 옵션
            <div className="space-y-4">
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setFormType('peer');
                  // Self Evaluation에서 선택한 팀을 Peer Evaluation에도 사용
                  if (selfData.team) {
                    handleTeamSelection(selfData.team);
                  }
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg font-semibold text-lg"
              >
                📝 Continue to Peer Evaluation
              </button>
              <Link 
                to="/admin/feedback" 
                className="block w-full bg-gray-500 text-white px-8 py-4 rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Back to Admin Panel
              </Link>
            </div>
          ) : (
            // Peer Evaluation 제출 후 - 메인으로
            <Link 
              to="/admin/feedback" 
              className="inline-block bg-blue-500 text-white px-8 py-4 rounded-lg hover:bg-blue-600 transition-colors font-semibold text-lg shadow-lg"
            >
              Back to Admin Panel
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (!formType) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-6">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">📝</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Staff Feedback Form</h1>
            <p className="text-gray-600">Choose the type of feedback you want to submit</p>
            {eventDetails && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="font-semibold text-blue-800">{eventDetails.title}</p>
                <p className="text-sm text-blue-600">
                  📅 {new Date(eventDetails.eventDateTime || eventDetails.date).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button 
              onClick={() => setFormType('self')} 
              className="w-full py-12 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              style={{
                minHeight: '200px',
                padding: '48px 24px',
                fontSize: '18px'
              }}
            >
              <div className="text-5xl mb-6" style={{fontSize: '3rem'}}>🔍</div>
              <div className="font-semibold text-2xl mb-3" style={{fontSize: '1.5rem', fontWeight: '600'}}>Self Evaluation</div>
              <div className="text-base opacity-90" style={{fontSize: '1rem'}}>Evaluate your own performance</div>
            </button>
            
            <button 
              onClick={() => setFormType('peer')} 
              className="w-full py-12 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              style={{
                minHeight: '200px',
                padding: '48px 24px',
                fontSize: '18px'
              }}
            >
              <div className="text-5xl mb-6" style={{fontSize: '3rem'}}>👥</div>
              <div className="font-semibold text-2xl mb-3" style={{fontSize: '1.5rem', fontWeight: '600'}}>Peer Evaluation</div>
              <div className="text-base opacity-90" style={{fontSize: '1rem'}}>Evaluate your team members</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="text-4xl mb-4">
            {formType === 'self' ? '🔍' : '👥'}
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            {formType === 'self' ? 'Self Evaluation' : 'Peer Evaluation'}
          </h1>
          <p className="text-gray-600 mt-2">for <span className="font-semibold text-indigo-600">{eventDetails?.title}</span></p>
          {eventDetails && (
            <div className="mt-2 text-sm text-gray-500">
              <p>📅 {new Date(eventDetails.eventDateTime || eventDetails.date).toLocaleDateString()}</p>
              <p>📍 {eventDetails.location}</p>
              <p>🏢 {eventDetails.branch}</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          {formType === 'self' ? (
            <form onSubmit={handleSelfSubmit} className="space-y-6">
              {/* Self Evaluation Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Team *</label>
                  <select
                    value={selfData.team}
                    onChange={(e) => {
                      const selectedTeam = e.target.value;
                      setSelfData(prev => ({...prev, team: selectedTeam, staffName: '', role: ''}));
                    }}
                    className="w-full p-2 border rounded-md"
                    required
                  >
                    <option value="">Select Your Team</option>
                    <option value="EP">Event Planning</option>
                    <option value="PR">Public Relations</option>
                    <option value="GA">General Affairs</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <select
                    value={selfData.staffName}
                    onChange={(e) => {
                      setSelfData(prev => ({
                        ...prev, 
                        staffName: e.target.value
                      }));
                    }}
                    className="w-full p-2 border rounded-md"
                    required
                    disabled={!selfData.team}
                  >
                    <option value="">Select Your Name</option>
                    {staffList
                      .filter(staff => staff.team.includes(selfData.team))
                      .map(staff => (
                        <option key={staff.id} value={staff.name}>
                          {staff.name} ({staff.role})
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Role *</label>
                <input
                  type="text"
                  value={selfData.role}
                  onChange={(e) => setSelfData(prev => ({...prev, role: e.target.value}))}
                  className="w-full p-2 border rounded-md"
                  placeholder="e.g., Shooting videos for Reels, Hosting event, Registration"
                  required
                />
              </div>

               {/* Self Rating Section */}
               <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                 <h3 className="font-semibold mb-6 text-center text-lg text-blue-800 flex items-center justify-center">
                   <span className="mr-2">⭐</span>
                   Rate Your Performance (1-5 stars)
                   <span className="ml-2">⭐</span>
                 </h3>
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center bg-white p-4 rounded-lg shadow-sm border border-blue-50">
                    <div className="text-2xl mb-2">🚀</div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Proactiveness
                    </label>
                    <p className="text-xs text-gray-600 mb-3">
                      How proactive were you in taking initiative?
                    </p>
                    <StarRating 
                      rating={selfData.proactiveness} 
                      setRating={(rating) => setSelfData(prev => ({...prev, proactiveness: rating}))}
                    />
                  </div>

                  <div className="text-center bg-white p-4 rounded-lg shadow-sm border border-blue-50">
                    <div className="text-2xl mb-2">✅</div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Task Completion
                    </label>
                    <p className="text-xs text-gray-600 mb-3">
                      How well did you complete your assigned tasks?
                    </p>
                    <StarRating 
                      rating={selfData.taskCompletion} 
                      setRating={(rating) => setSelfData(prev => ({...prev, taskCompletion: rating}))}
                    />
                  </div>

                  <div className="text-center bg-white p-4 rounded-lg shadow-sm border border-blue-50">
                    <div className="text-2xl mb-2">💬</div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Communication
                    </label>
                    <p className="text-xs text-gray-600 mb-3">
                      How effective was your communication?
                    </p>
                    <StarRating 
                      rating={selfData.communication} 
                      setRating={(rating) => setSelfData(prev => ({...prev, communication: rating}))}
                    />
                  </div>

                  <div className="text-center bg-white p-4 rounded-lg shadow-sm border border-blue-50">
                    <div className="text-2xl mb-2">⏰</div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time Management
                    </label>
                    <p className="text-xs text-gray-600 mb-3">
                      How well did you manage your time?
                    </p>
                    <StarRating 
                      rating={selfData.timeManagement} 
                      setRating={(rating) => setSelfData(prev => ({...prev, timeManagement: rating}))}
                    />
                  </div>
                </div>
              </div>

              {/* Self Reflection Questions */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    1. What was one challenge you faced? *
                  </label>
                  <textarea
                    rows="3"
                    value={selfData.teamChallenge}
                    onChange={(e) => setSelfData(prev => ({...prev, teamChallenge: e.target.value}))}
                    className="w-full p-2 border rounded-md"
                    placeholder="e.g., Last-minute changes, resource constraints, communication issues..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    2. What key lesson did you learn from it? *
                  </label>
                  <textarea
                    rows="3"
                    value={selfData.proudOf}
                    onChange={(e) => setSelfData(prev => ({...prev, proudOf: e.target.value}))}
                    className="w-full p-2 border rounded-md"
                    placeholder="e.g., Better planning is crucial, communication is key, flexibility helps..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    3. What is one thing you will do differently to improve next time? *
                  </label>
                  <textarea
                    rows="3"
                    value={selfData.improvements}
                    onChange={(e) => setSelfData(prev => ({...prev, improvements: e.target.value}))}
                    className="w-full p-2 border rounded-md"
                    placeholder="e.g., Start planning earlier, improve communication, delegate better..."
                    required
                  />
                </div>
              </div>

              <button type="submit" className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg">
                Submit Self Evaluation
              </button>
            </form>
          ) : (
            <div className="space-y-6">
              {!showTeamStaff ? (
                // Step 1: Team Selection
                <div className="text-center">
                  <div className="text-6xl mb-6">👥</div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Choose Your Team</h2>
                  <p className="text-gray-600 mb-8">Select your team to start peer evaluation</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                    <button
                      onClick={() => handleTeamSelection('EP')}
                      className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl hover:from-blue-100 hover:to-blue-200 transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="text-3xl mb-2">📅</div>
                      <div className="font-semibold text-blue-800">EP</div>
                      <div className="text-sm text-blue-600">Event Planning</div>
                    </button>
                    
                    <button
                      onClick={() => handleTeamSelection('PR')}
                      className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-xl hover:from-purple-100 hover:to-purple-200 transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="text-3xl mb-2">📢</div>
                      <div className="font-semibold text-purple-800">PR</div>
                      <div className="text-sm text-purple-600">Public Relations</div>
                    </button>
                    
                    <button
                      onClick={() => handleTeamSelection('GA')}
                      className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-xl hover:from-green-100 hover:to-green-200 transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="text-3xl mb-2">⚙️</div>
                      <div className="font-semibold text-green-800">GA</div>
                      <div className="text-sm text-green-600">General Affairs</div>
                    </button>
                  </div>
                </div>
              ) : (
                // Step 2: Staff Evaluation
                <form onSubmit={handlePeerSubmit} className="space-y-6">
                  <div className="text-center mb-6">
                    <div className="text-4xl mb-2">👥</div>
                    <h2 className="text-2xl font-bold text-gray-800">Evaluate Your Team Members</h2>
                    <p className="text-gray-600">Rate each team member and provide feedback</p>
                    <div className="mt-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-full inline-block">
                      Team: {selectedTeam}
                    </div>
                    
                  </div>

                  {/* Staff Evaluation Cards */}
                  <div className="space-y-6">
                    {teamStaffList.map((staff, index) => {
                      const evaluation = staffEvaluations[staff.name] || {};
                      const isExcluded = excludedStaff.has(staff.name);
                      return (
                        <div 
                          key={staff.id} 
                          className={`p-6 rounded-xl border shadow-sm ${isExcluded ? 'bg-gray-200 opacity-60' : 'bg-white border-gray-200'}`}
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                {staff.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-4">
                                <h3 className="text-lg font-semibold text-gray-800">{staff.name}</h3>
                                <p className="text-sm text-gray-600">{staff.role}</p>
                              </div>
                            </div>
                            
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isExcluded}
                                onChange={(e) => {
                                  const newExcludedStaff = new Set(excludedStaff);
                                  if (e.target.checked) {
                                    newExcludedStaff.add(staff.name);
                                  } else {
                                    newExcludedStaff.delete(staff.name);
                                  }
                                  setExcludedStaff(newExcludedStaff);
                                }}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">It's me</span>
                            </label>
                          </div>

                          {/* Rating Section */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                            <div className="text-center">
                              <div className="text-2xl mb-2">🎯</div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contribution
                              </label>
                              <StarRating 
                                rating={evaluation.contribution || 0} 
                                setRating={(rating) => updateStaffEvaluation(staff.name, 'contribution', rating)}
                                size="text-xl"
                                disabled={isExcluded}
                              />
                            </div>

                            <div className="text-center">
                              <div className="text-2xl mb-2">🤝</div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Teamwork
                              </label>
                              <StarRating 
                                rating={evaluation.teamwork || 0} 
                                setRating={(rating) => updateStaffEvaluation(staff.name, 'teamwork', rating)}
                                size="text-xl"
                                disabled={isExcluded}
                              />
                            </div>

                            <div className="text-center">
                              <div className="text-2xl mb-2">💬</div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Communication
                              </label>
                              <StarRating 
                                rating={evaluation.communication || 0} 
                                setRating={(rating) => updateStaffEvaluation(staff.name, 'communication', rating)}
                                size="text-xl"
                                disabled={isExcluded}
                              />
                            </div>
                          </div>

                          {/* Feedback Section */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                What did they do well? (Optional)
                              </label>
                              <textarea
                                rows="2"
                                value={evaluation.strengths || ''}
                                onChange={(e) => updateStaffEvaluation(staff.name, 'strengths', e.target.value)}
                                className="w-full p-2 border rounded-md text-sm"
                                placeholder="e.g., Great leadership, creative ideas..."
                                disabled={isExcluded}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Any suggestions for improvement? (Optional)
                              </label>
                              <textarea
                                rows="2"
                                value={evaluation.improvements || ''}
                                onChange={(e) => updateStaffEvaluation(staff.name, 'improvements', e.target.value)}
                                className="w-full p-2 border rounded-md text-sm"
                                placeholder="e.g., Could be more proactive..."
                                disabled={isExcluded}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Most Helpful Colleague Section */}
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-4 flex items-center">
                      <span className="text-2xl mr-2">🏆</span>
                      Who was the most helpful or impressive colleague in this event?
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-yellow-700 mb-1">
                          Select Colleague (Optional)
                        </label>
                         <select
                           value={peerData.mostHelpfulColleague}
                           onChange={(e) => setPeerData(prev => ({...prev, mostHelpfulColleague: e.target.value}))}
                           className="w-full p-2 border rounded-md"
                         >
                           <option value="">Select a colleague</option>
                           {staffList
                             .filter(staff => staff.role !== 'LEADER' && staff.role !== 'PRESIDENT')
                             .map(staff => (
                               <option key={staff.id} value={staff.name}>
                                 {staff.name} ({staff.role}) - {staff.team}
                               </option>
                             ))}
                         </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-yellow-700 mb-1">
                          Reason (Optional)
                        </label>
                        <textarea
                          rows="3"
                          value={peerData.mostHelpfulReason}
                          onChange={(e) => setPeerData(prev => ({...prev, mostHelpfulReason: e.target.value}))}
                          className="w-full p-2 border rounded-md"
                          placeholder="e.g., They showed exceptional leadership and helped everyone..."
                        />
                      </div>
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Submit All Evaluations
                  </button>
                </form>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <button 
              onClick={() => setFormType('')} 
              className="text-gray-500 hover:text-gray-700 underline"
            >
              ← Back to Form Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
